import React from "react";
import { FontIcon } from "@fluentui/react";
import { useFluentContext } from "@fluentui/react-northstar";
import { toTitleCase } from "../../utils/helper";
import type { TableSortConfig } from "./tableSortConfig";
import Styles from "./table.module.css";

export function TableSortHeaderIndicator({
  columnField,
  sortField,
  sortDirection,
}: {
  columnField: string;
  sortField: string;
  sortDirection: "asc" | "desc";
}) {
  // The ticket lists' arrow grey (default.foreground2), read from the theme: Fluent's fixed grey was dim on the dark
  // theme, and not every page sets the --mgt-theme-* variables the lists use (Lifecycle does not).
  const color = useFluentContext().theme.siteVariables.colorScheme?.default?.foreground2 ?? "rgb(96, 94, 92)";
  if (columnField !== sortField) {
    return null;
  }
  if (sortDirection === "asc") {
    return (
      <FontIcon
        iconName="SortUp"
        style={{
          marginLeft: ".35rem",
          flexShrink: 0,
          fontSize: 12,
          color,
        }}
      />
    );
  }
  return (
    <FontIcon
      iconName="SortDown"
      style={{
        marginLeft: ".35rem",
        flexShrink: 0,
        fontSize: 12,
        color,
      }}
    />
  );
}

function SortableHeaderTrigger({
  displayTitle,
  columnField,
  sortField,
  sortDirection,
  onActivate,
}: {
  displayTitle: React.ReactNode;
  columnField: string;
  sortField: string;
  sortDirection: "asc" | "desc";
  onActivate: () => void;
}) {
  const labelText =
    typeof displayTitle === "string" ? displayTitle : "column";
  return (
    <button
      type="button"
      className={Styles.sortableHeaderTrigger}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onActivate();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onActivate();
        }
      }}
      aria-label={`Sort by ${labelText}`}
    >
      <span>{displayTitle}</span>
      <TableSortHeaderIndicator
        columnField={columnField}
        sortField={sortField}
        sortDirection={sortDirection}
      />
    </button>
  );
}

export function buildSortableTableHeader(
  headers: string[],
  headerNotTitleCase: boolean,
  sortConfig: TableSortConfig<string>,
  sortField: string,
  sortDirection: "asc" | "desc",
  onSortHeaderClick: (field: string) => void
): HeaderList {
  const byHeaderKey = new Map(
    sortConfig.columns
      .filter((c) => c.headerKey != null && c.headerKey !== "")
      .map((c) => [c.headerKey, c])
  );
  const data: HeaderList = {
    key: "header",
    items: [],
    className: Styles.tableHeader,
  };
  headers.forEach((header, index) => {
    const key = `h-${index}-${header || "empty"}`;
    const col = header ? byHeaderKey.get(header) : undefined;
    const displayTitle =
      header && (headerNotTitleCase ? header : toTitleCase(header));
    if (!col) {
      data.items.push({
        content: displayTitle ? (
          <span style={{ lineHeight: "32px" }}>{displayTitle}</span>
        ) : (
          ""
        ),
        key,
      });
      return;
    }
    data.items.push({
      key,
      content: (
        <SortableHeaderTrigger
          displayTitle={displayTitle}
          columnField={col.field}
          sortField={sortField}
          sortDirection={sortDirection}
          onActivate={() => onSortHeaderClick(col.field)}
        />
      ),
    });
  });
  return data;
}

/** For `Table` / `Table.Cell` header rows (e.g. lifecycle). */
export function SortableColumnHeader({
  label,
  columnField,
  sortField,
  sortDirection,
  onSortClick,
}: {
  label: React.ReactNode;
  columnField: string;
  sortField: string;
  sortDirection: "asc" | "desc";
  onSortClick: (field: string) => void;
}) {
  return (
    <SortableHeaderTrigger
      displayTitle={label}
      columnField={columnField}
      sortField={sortField}
      sortDirection={sortDirection}
      onActivate={() => onSortClick(columnField)}
    />
  );
}
